// "Scan storefront colors" - the mapping UI in front of paletteTokenize.ts.
//
// Opened from inside a palette's editor: it lists every literal colour the storefront still
// carries, grouped by distinct colour with a usage count, and lets each one be pointed at a slot of
// THIS palette (or skipped). Applying rewrites those values to `$palette:<slot>` tokens, so the
// palette becomes the one place they are set.
//
// Colours that already match a slot of the palette are pre-mapped to it, which is the common case
// right after a merchant has built the palette by eye from the colours already on the store.

import React from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    PrimaryButton,
    Stack,
    useTheme,
} from '@fluentui/react';
import { IStorefrontSettings, IStorefrontStyle } from '../../../models/Storefront';
import { IColorTokenMapping, IColorUsage, parseColorValue, scanStorefrontColors } from './paletteTokenize';
import { PALETTE_SLOT_LABELS, PALETTE_SLOTS, PaletteSlot } from './stylesConfig';

const SKIP_KEY = '__skip__';

interface IPaletteTokenizeDialogProps {
    hidden: boolean;
    onDismiss: () => void;
    /** The palette being edited - its slots are the mapping targets. */
    palette: IStorefrontStyle;
    /** The pages editor's working settings (typed style fields + page-config blobs). */
    settings?: IStorefrontSettings;
    /** The named styles to scan (the palette/font-pack docs are skipped by the scanner). */
    styles: IStorefrontStyle[];
    busy?: boolean;
    onApply: (mapping: IColorTokenMapping) => void;
}

const swatchStyle = (color: string, alpha: number): React.CSSProperties => ({
    width: 26,
    height: 26,
    borderRadius: 3,
    flexShrink: 0,
    border: '1px solid rgba(0,0,0,0.25)',
    // A translucent colour is shown over a checkerboard so it doesn't read as a lighter solid.
    backgroundColor: color,
    opacity: alpha,
});

const PaletteTokenizeDialog: React.FC<IPaletteTokenizeDialogProps> = (props) => {
    const theme = useTheme();
    const { hidden, palette, settings, styles } = props;

    // Rescan every time the dialog opens: the working settings and the styles list both move
    // underneath it. Keyed on `hidden` so a closed dialog costs nothing.
    const usages: IColorUsage[] = React.useMemo(
        () => (hidden ? [] : scanStorefrontColors(settings, styles)),
        [hidden, settings, styles]);

    const paletteColors = (palette.parts?.colors ?? {}) as Record<string, unknown>;

    // Pre-map any colour that already equals one of this palette's slots - the common case right
    // after building the palette by eye from the colours already on the store. Derived from
    // `palette` itself rather than from the `paletteColors` alias, which is a fresh object per
    // render and would re-run this on every keystroke.
    const suggested = React.useMemo(() => {
        const slotColors = (palette.parts?.colors ?? {}) as Record<string, unknown>;
        const bySlotColor = new Map<string, PaletteSlot>();
        for (const slot of PALETTE_SLOTS) {
            const parsed = parseColorValue(slotColors[slot]);
            if (parsed && !bySlotColor.has(parsed.hex)) bySlotColor.set(parsed.hex, slot);
        }
        const map: IColorTokenMapping = {};
        for (const usage of usages) {
            const slot = bySlotColor.get(usage.hex);
            if (slot) map[usage.key] = slot;
        }
        return map;
    }, [usages, palette]);

    const [mapping, setMapping] = React.useState<IColorTokenMapping>({});
    // Seed from the suggestion whenever the scan is refreshed (i.e. each time the dialog opens).
    React.useEffect(() => { setMapping(suggested); }, [suggested]);

    const slotOptions: IDropdownOption[] = [
        { key: SKIP_KEY, text: 'Skip' },
        ...PALETTE_SLOTS
            .filter((slot) => typeof paletteColors[slot] === 'string' && !!paletteColors[slot])
            .map((slot) => ({ key: slot, text: PALETTE_SLOT_LABELS[slot] })),
    ];

    const mappedCount = usages.filter((u) => !!mapping[u.key]).length;
    const affected = usages.reduce((sum, u) => sum + (mapping[u.key] ? u.count : 0), 0);

    return (
        <Dialog
            hidden={hidden}
            onDismiss={props.onDismiss}
            minWidth={520}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Scan storefront colors',
                subText: `Every color still written out by hand on this storefront. Point one at a palette slot and it becomes a link to "${palette.name}" - change the palette later and every one of them follows.`,
            }}
        >
            {usages.length === 0 ? (
                <div style={{ fontSize: 12, color: theme.palette.neutralSecondary }}>
                    No literal colors found. Everything on this storefront is already using the palette,
                    inheriting from the theme, or set somewhere this scan does not reach.
                </div>
            ) : (
                <Stack tokens={{ childrenGap: 4 }} style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {usages.map((usage) => (
                        <Stack
                            key={usage.key}
                            horizontal
                            verticalAlign='center'
                            tokens={{ childrenGap: 8 }}
                            style={{ padding: '4px 0', borderBottom: `1px solid ${theme.palette.neutralLighter}` }}
                        >
                            <span style={swatchStyle(usage.hex, usage.alpha)} />
                            <Stack grow style={{ minWidth: 0 }}>
                                <span style={{ fontSize: 13, fontFamily: 'monospace' }}>
                                    {usage.hex}{usage.alpha === 1 ? '' : ` @ ${usage.alpha}`}
                                </span>
                                <span style={{ fontSize: 11, color: theme.palette.neutralSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {usage.count} use{usage.count === 1 ? '' : 's'} · {usage.sources.join(', ')}
                                </span>
                            </Stack>
                            <Dropdown
                                options={slotOptions}
                                selectedKey={mapping[usage.key] ?? SKIP_KEY}
                                ariaLabel={`Palette slot for ${usage.hex}`}
                                onChange={(_, o) => setMapping((prev) => ({
                                    ...prev,
                                    [usage.key]: o?.key === SKIP_KEY ? undefined : (o?.key as PaletteSlot),
                                }))}
                                styles={{ root: { width: 170 } }}
                            />
                        </Stack>
                    ))}
                </Stack>
            )}
            <div style={{ fontSize: 11, color: theme.palette.neutralSecondary, marginTop: '0.5em' }}>
                Covers the storefront&apos;s own colors, the page layouts and the named styles. Sections keep
                their own styling - use each section&apos;s Appearance tab to point those at the palette.
                The page and settings changes are draft edits: they persist when you exit Edit Mode.
            </div>
            <DialogFooter>
                <PrimaryButton
                    text={affected > 0 ? `Apply to ${affected} value${affected === 1 ? '' : 's'}` : 'Apply'}
                    disabled={props.busy || mappedCount === 0}
                    onClick={() => props.onApply(mapping)}
                />
                <DefaultButton text='Cancel' disabled={props.busy} onClick={props.onDismiss} />
            </DialogFooter>
        </Dialog>
    );
};

export default PaletteTokenizeDialog;
